import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import Highlighter from 'react-highlight-words';
import CollapsibleItemGrid, { ITEMS_TO_SHOW } from './CollapsibleItemGrid';
import css from './GroupedContent.css';

const GROUPS_TO_SHOW = 2;

const defaultRenderItem = (item, search) => (
    <Highlighter searchWords={[search]} textToHighlight={item.searchText} autoEscape />
);

const GroupedContent = ({ data, columns = 4, renderItem = defaultRenderItem }) => {
    const totalItems = data.reduce((sum, group) => sum + group.items.length, 0);
    const needsCollapse = totalItems > ITEMS_TO_SHOW || data.length > GROUPS_TO_SHOW;
    const totalCount = needsCollapse ? Math.max(totalItems, ITEMS_TO_SHOW + 1) : totalItems;

    const renderContent = ({ expanded, search, contentHolderClassName }) => {
        let shownCount = 0;
        let groupCount = 0;
        const searchLower = expanded && search ? search.toLowerCase().trim() : '';

        return (
            <div className={classNames(contentHolderClassName, css.groupsHolder)}>
                {data.map(group => {
                    let items;

                    if (searchLower) {
                        const titleMatch = group.title.toLowerCase().includes(searchLower);
                        items = titleMatch
                            ? group.items
                            : group.items.filter(item => (item.searchText || '').toLowerCase().includes(searchLower));
                        if (items.length === 0) return null;
                    } else if (!expanded) {
                        if (groupCount >= GROUPS_TO_SHOW || shownCount >= ITEMS_TO_SHOW) return null;
                        const remaining = ITEMS_TO_SHOW - shownCount;
                        items = group.items.slice(0, remaining);
                        shownCount += items.length;
                        groupCount++;
                    } else {
                        items = group.items;
                    }

                    const highlightedTitle = searchLower ? (
                        <Highlighter searchWords={[search]} textToHighlight={group.title} autoEscape />
                    ) : (
                        group.title
                    );

                    return (
                        <div key={group.key} className={css.groupArea}>
                            <div className={css.groupTitle}>{highlightedTitle}</div>
                            <div className={css.itemsList} style={{ '--columns': columns }}>
                                {items.map(item => (
                                    <div key={item.key} className={css.item}>
                                        {renderItem(item, search)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return <CollapsibleItemGrid totalCount={totalCount} renderContent={renderContent} />;
};

GroupedContent.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    key: PropTypes.string.isRequired,
                    searchText: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
    columns: PropTypes.number,
    renderItem: PropTypes.func,
};

export default GroupedContent;
